import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { CollapsibleCard } from '../ui/collapsible-card';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Gift, Zap, Lock, ChevronRight, Trophy, Flame } from 'lucide-react';

const FRAME_LABEL = { bronze: 'Cadre Bronze', silver: 'Cadre Argent', gold: 'Cadre Or', diamond: 'Cadre Diamant' };

const XPRewardsCard = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get('/gamification/xp-summary')
      .then(res => { if (mounted) setSummary(res.data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [api]);

  if (!summary) return null;

  return (
    <CollapsibleCard
      id="xpRewards"
      title="Mon XP & Récompenses"
      icon={<Zap className="w-5 h-5 text-primary" />}
      headerRight={
        <Badge variant="outline" className="text-xs font-semibold" data-testid="xp-widget-level-badge">
          Niv. {summary.level}
        </Badge>
      }
    >
      <div className="space-y-4" data-testid="xp-rewards-widget">
        <div className="flex items-end justify-between">
          <div>
            <p className="font-heading text-lg leading-none">{summary.title}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {summary.points.toLocaleString('fr-FR')} XP cumulés
              {summary.earned_today > 0 && (
                <span className="text-primary font-medium"> · +{summary.earned_today} aujourd'hui</span>
              )}
            </p>
          </div>
          {summary.streak > 0 && (
            <span className="flex items-center gap-1 text-xs text-muted-foreground" data-testid="xp-widget-streak">
              <Flame className="w-3.5 h-3.5 text-primary" />
              {summary.streak} j
            </span>
          )}
        </div>

        <div className="space-y-1.5">
          <Progress value={summary.progress_percent} className="h-3" data-testid="xp-progress-bar" />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{summary.progress_percent}%</span>
            <span data-testid="xp-remaining">
              {summary.xp_next_level
                ? `${summary.xp_remaining.toLocaleString('fr-FR')} XP → niveau ${summary.level + 1}`
                : 'Niveau maximum atteint'}
            </span>
          </div>
        </div>

        {summary.pending_rewards > 0 && (
          <button
            onClick={() => navigate('/rewards')}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-primary/40 bg-primary/5 text-sm font-medium hover:bg-primary/10 transition-colors"
            data-testid="xp-widget-pending-rewards"
          >
            <Gift className="w-4 h-4 text-primary" />
            {summary.pending_rewards} récompense{summary.pending_rewards > 1 ? 's' : ''} à réclamer
            <ChevronRight className="w-4 h-4 ml-auto" />
          </button>
        )}

        {summary.next_rewards?.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
              <Trophy className="w-3.5 h-3.5" />
              Prochaines récompenses
            </p>
            {summary.next_rewards.map((reward) => (
              <div
                key={reward.level}
                className="flex items-start gap-3 p-2.5 rounded-lg border border-border bg-secondary/20"
                data-testid={`next-reward-${reward.level}`}
              >
                <div className="w-9 h-9 rounded-lg bg-secondary flex flex-col items-center justify-center flex-shrink-0">
                  <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{reward.title}</p>
                  <p className="text-xs text-muted-foreground">
                    Niveau {reward.level} · {(reward.xp_required - summary.points).toLocaleString('fr-FR')} XP restants
                    {reward.frame && ` · ${FRAME_LABEL[reward.frame] || reward.frame}`}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => navigate('/rewards')}
          data-testid="xp-widget-see-all"
        >
          Voir toutes les récompenses
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </CollapsibleCard>
  );
};

export default XPRewardsCard;
